import { getCountertopRunIsland, getGroupCountertopEdge } from '../helpers';
import { Vertex3 } from '../../../spatial-classes';

import type { Breakpoint } from '../breakpoint.class';
import type { PanelItemGroup } from '../../panel-item-groups/panel-item-group.class';
import type { PanelItemGroups } from '../../panel-item-groups/panel-item-groups.class';

export type BreakpointSnapDirection = 'left' | 'right';

type AdjacentGroupParams = {
	panelItemGroups: PanelItemGroups;
	center: number;
	direction: BreakpointSnapDirection;
	withCountertopItems: boolean;
};

const findAdjacentGroup = (params: AdjacentGroupParams): PanelItemGroup | null => {
	const { panelItemGroups, center, direction, withCountertopItems } = params;
	const from = new Vertex3({ dataIn: { x: center, y: 0, z: 0 }});

	return direction === 'left'
		? panelItemGroups.findPreviousByCenter(from, withCountertopItems)
		: panelItemGroups.findNextByCenter(from, withCountertopItems);
};

/**
 * Moves the breakpoint onto the facing edge of the appliance column next to it -- snapping left
 * lands on the right edge of the column to its left, and vice versa. Panel and backsplash
 * breakpoints snap to the column's full width; countertop breakpoints snap to the width of the
 * cutout it carves into the top. A direction with no column, or a column that reaches nothing on
 * the countertop, leaves the breakpoint where it is.
 * @param breakpoint the breakpoint to move
 * @param direction which neighbour to snap against
 */
export const snapToAdjacentGroupEdgeCfn = (breakpoint: Breakpoint, direction: BreakpointSnapDirection): void => {
	const { panel = null, countertop = null } = breakpoint.refMap ?? {};

	// Panel/Backsplash breakpoints take priority (backsplash refMap has panel reference due to inheritance)
	if (panel?.breakpoints?.children?.some((child) => child.id === breakpoint.id)) {
		const group = findAdjacentGroup({ panelItemGroups: panel.panelItemGroups, center: breakpoint.center, direction, withCountertopItems: false });
		if (!group) return;

		const halfGroupWidth = group.maxWidth / 2;
		breakpoint.center = direction === 'left' ? group.center.x + halfGroupWidth : group.center.x - halfGroupWidth;
		return;
	}

	if (!countertop?.breakpoints?.children?.some((child) => child.id === breakpoint.id)) return;

	const frontPanel = getCountertopRunIsland(countertop)?.panels?.getFrontPanel() ?? null;
	if (!frontPanel) return;

	const group = findAdjacentGroup({ panelItemGroups: frontPanel.panelItemGroups, center: breakpoint.center, direction, withCountertopItems: true });
	if (!group) return;

	const edge = getGroupCountertopEdge({ group, side: direction === 'left' ? 'right' : 'left' });
	if (edge === null) return;

	breakpoint.center = edge;
};
